import { Fragment, useRef, useState } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Divider, Text } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';
import Badge from '../../Components/Badge';
import EmptyData from '../../Components/EmptyData';
import Footer from '../../Components/Footer';
import ScreenBanner from '../../Components/ScreenBanner';
import SwitchTabOrderType from '../../Components/SwitchTabOrderType';
import Cart from '../../Components/Web/Cart/CartList';
import { Section } from '../../Components/Web/Items';
import { getEnvValue } from '../../Helper/env';
import ImageHelper from '../../Helper/Image';
import ScreenSize from '../../Helper/ScreenSize';
import useError from '../../Hooks/useError';
import { shopLoadingSelector, shopSelector } from '../../RTK/shop/selectors';
import ThemeColor from '../../Theme/colors';
import ThemeStyle from '../../Theme/styles';
import BreakPoints from '../../Theme/styles/breakpoints';

import useController from './controller';
import ExclusiveSectionBadge from './ExclusiveSectionBadge';
import FeaturedItems from './FeaturedItems';
import FloatingButtonMobileView from './FloatingButtonMobileView';
import useListSectionItemController from './ListSectionItem/controller/ListSection.controller';
import Loader from './Loader';
import LoaderMenu from './Loader/Menu';
import MealPlanHint, { HintKey } from './MealPlanHint';
import MenuSearch from './MenuSearch';
import PromotionService from './PromotionService';
import StoreDescription from './StoreDescription';
import {
  ids as storeStyleIds,
  styles as storeStyles,
} from './stylesResponsive';
import TabSectionList from './TabSectionList/TabSectionList';

const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');
const isBranded = RAZZLE_BUILD_MODE === 'branded';

const Store = (props) => {
  const scrollRef = useRef();
  const xxl = useMediaQuery({ query: '(min-width: 1400px' });

  //REDUX
  const shopData = useSelector(shopSelector);
  const shopLoading = useSelector(shopLoadingSelector);

  ///STATES
  const [search, setSearch] = useState('');
  const [showSearchModal, setSearchModal] = useState(false);
  const { searchItems, searchItemByName, onItemPress } =
    useListSectionItemController();
  const { ErrorCartOrCheckoutHandler } = useError(shopData?.id);
  const { xs2 } = ScreenSize();
  const {
    state: { isGettingMenu },
  } = useController(props);

  const onChangeText = (nextValue) => {
    setSearch(nextValue);
    searchItemByName(nextValue);
  };

  const storeLogo = ImageHelper.getStoreLogoImage(shopData || {});

  const onPressSearch = () => setSearchModal(!showSearchModal);

  const renderSectionTitle = ({
    section: { title, subTitle, isExclusive, categoryStock },
  }) => (
    <View key={title} style={{ backgroundColor: ThemeColor.white }}>
      <View
        dataSet={{ media: ids.sectionTitleContainer }}
        style={[styles.sectionTitleContainer, ThemeStyle.spacingTop]}
      >
        <Text category="s1">{title}</Text>
        <View
          style={[
            ThemeStyle.flexDirectionRowCenter,
            ThemeStyle.spacingLeftMedium,
          ]}
        >
          {!!categoryStock && (
            <Badge
              type={Badge.TYPES.SOLID}
              category={Badge.CATEGORIES.DANGER}
              size="c1"
            >
              {`${categoryStock} items left!`}
            </Badge>
          )}
          <ExclusiveSectionBadge
            isExclusive={isExclusive}
            scrollRef={scrollRef}
          />
        </View>
      </View>
      {!!subTitle && (
        <View style={styles.sectionSubtitleContainer}>
          <View
            style={
              isExclusive
                ? styles.exclusiveContainer
                : ThemeStyle.spacingTopSmall
            }
          >
            <Text
              category="p2"
              style={[ThemeStyle.dimColor, isExclusive && styles.exclusiveText]}
            >
              {subTitle}
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSectionItem = (item) => (
    <Section
      {...item}
      isPreview={shopData?.is_preview ?? false}
      scrollRef={scrollRef}
      onError={(err) =>
        ErrorCartOrCheckoutHandler({
          error: err,
          isAcceptingAdvanceOrder: shopData?.is_accepting_in_advanced_orders,
        })
      }
      onItemPress={onItemPress}
    />
  );

  const renderEmpty = () => {
    if (isGettingMenu) {
      return null;
    }

    return (
      <View style={[ThemeStyle.spacingTop, ThemeStyle.pageHorizontalSpacing]}>
        <EmptyData type="filter" />
      </View>
    );
  };

  return (
    <View style={[ThemeStyle.flex1, ThemeStyle.flexDirectionRow]}>
      <View style={styles.wrapperFirstColumn}>
        <TabSectionList
          onRef={(r) => (scrollRef.current = r)}
          data={shopData?.menu_list || []}
          search={search}
          searchResult={searchItems?.menu_list || []}
          onSearch={onChangeText}
          onPressSearch={onPressSearch}
          ListHeaderComponent={() =>
            shopLoading ? (
              <Loader />
            ) : (
              <Fragment>
                <View
                  dataSet={{ media: ids.headerContent }}
                  style={[
                    styles.headerContent,
                    Boolean(storeLogo) ? styles.hasLogo : styles.hasNoLogo,
                  ]}
                >
                  <View
                    dataSet={{ media: storeStyleIds.wrapper }}
                    style={[storeStyles.wrapper, ThemeStyle.paddingLeft]}
                  >
                    <ScreenBanner
                      banner={ImageHelper.getStoreBannerImage(shopData)}
                      store_logo={storeLogo}
                    />
                  </View>

                  <StoreDescription />
                </View>
                {!isBranded && <PromotionService />}

                {isGettingMenu && <LoaderMenu />}

                {!isBranded && !lodashIsEmpty(shopData?.featured_items) && (
                  <Fragment>
                    <Divider style={ThemeStyle.divider} />
                    <MealPlanHint
                      hintKey={HintKey.FEATURED}
                      scrollRef={scrollRef}
                    >
                      <FeaturedItems onPress={onItemPress} />
                    </MealPlanHint>
                  </Fragment>
                )}
              </Fragment>
            )
          }
          renderSectionItem={renderSectionItem}
          renderSectionTitle={renderSectionTitle}
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={() => (
            <View
              dataSet={{ media: ids.footerContainer }}
              style={[styles.footerContainer, ThemeStyle.spacingTop]}
            >
              <Footer />
            </View>
          )}
        />
      </View>

      {/* second column */}
      {xxl && (
        <View
          dataSet={{ media: ids.wrapperSecondColumn }}
          style={styles.wrapperSecondColumn}
        >
          <SwitchTabOrderType size="medium" showMealPlan />
          <Cart
            isCartLoading={shopLoading}
            isClickableHeader={false}
            onItemPress={(item) => onItemPress({ ...item, isUpdate: true })}
          />
        </View>
      )}

      <MenuSearch
        show={showSearchModal && xs2}
        onHide={onPressSearch}
        onPress={(item) => {
          onItemPress(item);
        }}
      />

      <FloatingButtonMobileView />
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  wrapperFirstColumn: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    backgroundColor: ThemeColor.white,
  },
  wrapperSecondColumn: {
    width: 400,
    height: '100%',
    paddingTop: 10,
    paddingLeft: 15,
    paddingRight: 15,
    paddingBottom: 10,
    borderLeftColor: ThemeColor.gray100,
    borderLeftWidth: 1,
    [`@media ${BreakPoints.xs}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.sm}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.md}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.lg}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.xl}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xxl}`]: {
      display: 'flex',
    },
  },
  confirmModalWrapper: {
    width: 400,
    height: 'auto',
    flexDirection: 'column',
    padding: '0%',
  },
  removeButtonContainer: {
    backgroundColor: ThemeColor.green,
    width: 100,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    ...ThemeStyle.fullRoundCorder,
  },
  cancelButtonContainer: {
    backgroundColor: ThemeColor.white,
    width: 100,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    ...ThemeStyle.fullRoundCorder,
  },
  checkoutTxt: { ...ThemeStyle.medium, color: ThemeColor.white },
  cancelTxt: { ...ThemeStyle.medium, color: ThemeColor.black },
  singleContentContainer: {
    alignItems: 'center',
  },
  singleContentBold: { fontSize: 20, textAlign: 'center', fontWeight: 'bold' },
  singleContentRegular: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 10,
  },
  controlContainer: {
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexDirection: 'row',
    paddingHorizontal: 20,
    marginTop: 20,
  },
  controlButtonSeparator: {
    width: 30,
  },
  inputSearchWrapper: {
    ...ThemeStyle.spacingTopMedium,
    ...ThemeStyle.pageHorizontalSpacingSmall,
    alignSelf: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  searchModalContainer: {
    justifyContent: 'flex-start',
    margin: 0,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
    position: 'relative',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 100,
    paddingHorizontal: 10,
  },
  section_selected: {
    backgroundColor: ThemeColor.white,
    justifyContent: 'center',
    alignItems: 'center',
    height: '12%',
    borderBottomWidth: 3,
    borderBottomColor: ThemeColor.green,
  },
  section_unselected: {
    backgroundColor: ThemeColor.white,
    justifyContent: 'center',
    alignItems: 'center',
    height: '12%',
    borderBottomWidth: 0,
    borderBottomColor: null,
  },
  textInputStyle: {
    ...ThemeStyle.regular,
    fontSize: 14,
  },
  item: {
    backgroundColor: '#f9c2ff',
    padding: 20,
    width: '49%',
    height: '80%',
    marginVertical: 10,
  },

  title: {
    fontSize: 24,
  },
  searchInput: {
    borderColor: ThemeColor.gray,
    borderRadius: 30,
    borderWidth: 1,
    width: '100%',
  },
  searchIcon: { width: 20, height: 20 },
  icon_size: { width: 15, height: 15 },
  skeleton_container: { display: 'flex' },
  full_menu_txt: {
    ...ThemeStyle.bold,
    fontSize: 20,
  },
  sectionTitleContainer: {
    ...ThemeStyle.flexDirectionRowCenter,
    justifyContent: 'space-between',
    backgroundColor: ThemeColor.white,
    [`@media ${BreakPoints.xs}`]: ThemeStyle.pageHorizontalSpacing,
  },
  sectionSubtitleContainer: {
    padding: 10,
  },
  exclusiveContainer: {
    ...ThemeStyle.spacingTopMedium,
    ...ThemeStyle.pageHorizontalSpacingSmall,
    ...ThemeStyle.pageVerticalSpacingSmall,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: ThemeColor.blue,
  },
  exclusiveText: {
    color: ThemeColor.blue,
  },
  headerContent: {
    ...ThemeStyle.spacingTop,
    [`@media ${BreakPoints.xs}`]: {
      ...ThemeStyle.spacingRight,
    },
  },
  hasLogo: {
    rowGap: 40,
  },
  hasNoLogo: {
    rowGap: 10,
  },
  footerContainer: {
    [`@media ${BreakPoints.xs}`]: {
      marginHorizontal: 20,
    },
    [`@media ${BreakPoints.sm}`]: {
      marginHorizontal: 50,
      paddingHorizontal: 20,
    },
    [`@media ${BreakPoints.md}`]: {
      marginHorizontal: 50,
      paddingHorizontal: 20,
    },
    [`@media ${BreakPoints.lg}`]: {
      marginHorizontal: 50,
      paddingHorizontal: 20,
    },
    [`@media ${BreakPoints.xl}`]: {
      marginHorizontal: 50,
      paddingHorizontal: 20,
    },
    [`@media ${BreakPoints.xxl}`]: {
      marginHorizontal: 50,
      paddingHorizontal: 20,
    },
  },
});

export default Store;
